import { z } from 'zod/v4';
import { ActionResourceSchema } from './action-resource.js';

export const LIST_ACTIONS_DEFAULT_PAGE_SIZE = 10;

/**
 * The largest page the list will serve. A bigger `count` is **clamped**, not
 * rejected — the dashboard has always answered 200 with a capped page, and a
 * 400 here would break callers that ask for more.
 */
export const LIST_ACTIONS_MAX_PAGE_SIZE = 100;

export const ListActionsQuerySchema = z.object({
  count: z
    .preprocess(
      val => val ?? undefined,
      z.coerce.number().int().min(1).optional(),
    )
    .default(LIST_ACTIONS_DEFAULT_PAGE_SIZE)
    .transform(value => Math.min(value, LIST_ACTIONS_MAX_PAGE_SIZE)),
  page: z
    .preprocess(
      val => val ?? undefined,
      z.coerce.number().int().min(0).optional(),
    )
    .default(0),
  sort: z.string().optional(),
  filter: z
    .string()
    .max(512)
    .optional()
    .describe(
      'JSON string with filter criteria for corrective and preventive actions',
    ),
  quickFilter: z
    .enum(['all', 'internal', 'external', 'assignedToMe', 'reportedByMe'])
    .optional()
    .default('all')
    .describe(
      "Quick filter options: 'all' for all accessible actions, 'internal' for actions owned by your company, 'external' for actions owned by other companies, 'assignedToMe' for actions assigned to you, 'reportedByMe' for actions where you are the reporter",
    ),
});

export const ListActionsResponseSchema = z.object({
  data: z.array(ActionResourceSchema),
  meta: z.object({
    count: z.number(),
    page: z.number(),
    pageSize: z.number(),
    totalCount: z.number(),
  }),
});

export type ListActionsQuery = z.infer<typeof ListActionsQuerySchema>;
export type ListActionsResponse = z.infer<typeof ListActionsResponseSchema>;
